/**
 * The public text-messaging policy.
 *
 * 🔴 Written because the firm's business-texting registration was rejected on 17 Sep 2026 with
 * "unverifiable website". Before US carriers deliver business texts they send a reviewer to the
 * site to find how clients agree to receive them, what is sent, and how to stop. Nothing on the
 * site said any of it.
 *
 * The wording is FETCHED from the server rather than written here, so this page and the consent a
 * client is asked to accept in the portal can never say different things. A policy page that has
 * drifted from the agreement is worse than no page: it is what a carrier, or a regulator, would
 * read as the firm's stated practice.
 *
 * No login. That is the point — the reviewer has no account.
 */
import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Container, Divider, Paper, Typography } from '@mui/material';

interface Policy {
  title: string;
  version: string;
  summary: string;
  body: string;
}

const TextMessagePolicyView: React.FC = () => {
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.API_HOST || ''}/api/v1/public/policy/text-messages`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data) => {
        if (!cancelled) setPolicy(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Container maxWidth="md" sx={{ py: 5 }}>
      <Paper elevation={1} sx={{ p: { xs: 3, md: 4 } }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Text messages from The Law Offices of Dr. Lloyd A. Newton
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          We text clients about their own case, and only when they have agreed to it.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Typography variant="h6" component="h2" gutterBottom>
          How clients agree
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          A client is asked once, by name, when they sign in to our client portal. The question is
          separate from everything else they are asked, it can be declined, and declining changes
          nothing about their case or what we charge. Nobody is added to messages by filling in a
          form or by giving us a telephone number.
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom>
          Stopping messages
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Reply <strong>STOP</strong> to any message and they end immediately. Reply{' '}
          <strong>HELP</strong> for help, or call the office on 210.210.1616. Stopping the texts does
          not stop us contacting a client — anything urgent still reaches them by email or a phone
          call.
        </Typography>

        <Typography variant="h6" component="h2" gutterBottom>
          Your number
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          We do not sell, rent or share mobile numbers with anyone for their own marketing. Message
          and data rates may apply, charged by your phone company rather than by us.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Typography variant="h6" component="h2" gutterBottom>
          What a client is asked to agree to
        </Typography>

        {failed ? (
          <Typography variant="body1" color="text.secondary">
            The current wording could not be loaded. Please call the office on 210.210.1616 and we
            will send it to you.
          </Typography>
        ) : !policy ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {/*
              `pre-line` because the body is assembled as separate lines joined with "\n" on the
              server and HTML collapses those, which ran every bullet into one paragraph on the
              consent screen until it was fixed there for the same reason.
            */}
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}>
              {policy.body}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
              Version {policy.version}
            </Typography>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default TextMessagePolicyView;
